import { AxiosRequestConfig } from 'axios';

import instance from '../../../common/http/axiosInstance';
import { mapToData } from '../../../common/http/mapToData';
import { mapToErrors } from '../../../common/http/mapToErrors';
import { ConversionDetails, ConversionLogs, EventLogs, EventSearchResult } from '../domain';
import {
  ConversionDetailsResponse,
  ConversionLogsResponse,
  ConversionSearchDTO,
  EventLogsResponse,
  EventSearchDTO,
  EventSearchResultResponse,
  ExportConversionsDTO,
  PayoutPoolDetailsResponse,
  PayoutPoolItemsResponse,
  PayoutPoolLogsDTO,
  PayoutPoolLogsResponse,
  PayoutPoolPayoutsResponse,
  SearchConversionsResponse,
} from './dtos';
import {
  mapToConversionDetails,
  mapToConversionSearchResults,
  mapToEventLogs,
  mapToEventSearchResult,
  mapToFraudSearchResults,
} from './mappers';

const requests = {
  get: async <T>(url: string, config?: AxiosRequestConfig): Promise<T> =>
    instance.get(url, config).then(mapToData).catch(mapToErrors),
  post: async <T, K>(url: string, body: T): Promise<K> => instance.post(url, body).then(mapToData).catch(mapToErrors),
};

export interface GetFraudTotalsResponse {
  total_amounts: {
    currency_name: string;
    total_amount: string;
  }[];
  total_unique_users: number;
  total_frauds: number;
}

export const logService = {
  getEventLogs: async (projectId: string, searchParams: EventSearchDTO): Promise<EventLogs> => {
    return requests
      .get<EventLogsResponse>(`projects/${projectId}/events`, { params: { ...searchParams } })
      .then((response) => mapToEventLogs(response));
  },
  getEventSearchResult: async (projectId: string, eventId: string): Promise<EventSearchResult> => {
    return requests
      .get<EventSearchResultResponse>(`projects/${projectId}/events/${eventId}`)
      .then((response) => mapToEventSearchResult(response));
  },
  getConversionLogs: async (projectId: string, searchParams: ConversionSearchDTO): Promise<ConversionLogs> => {
    return requests
      .get<ConversionLogsResponse>(`projects/${projectId}/search/conversions`, {
        params: { ...searchParams },
      })
      .then((response) => mapToConversionSearchResults(response));
  },
  getFraudLogs: async (projectId: string, searchParams: ConversionSearchDTO): Promise<ConversionLogs> => {
    return requests
      .get<SearchConversionsResponse>(`projects/${projectId}/search/conversions`, {
        params: { ...searchParams },
      })
      .then((response) => mapToFraudSearchResults(response));
  },
  getFraudTotals: async (projectId: string, searchParams: Record<string, string>): Promise<GetFraudTotalsResponse> => {
    return requests
      .get<GetFraudTotalsResponse>(`projects/${projectId}/payouts/analytics/fraud-totals`, {
        params: { ...searchParams },
      })
      .then((response) => response);
  },
  getConversionDetails: async (projectId: string, attributionId: string): Promise<ConversionDetails> => {
    return requests
      .get<ConversionDetailsResponse>(`projects/${projectId}/search/conversions/${attributionId}`)
      .then((response) => mapToConversionDetails(response));
  },
  exportCSV: async (projectId: string, dto: ExportConversionsDTO): Promise<void> =>
    requests.post<ExportConversionsDTO, void>(`projects/${projectId}/search/conversions/export`, dto),

  getPayoutPoolLogs: async (projectId: string, dto: PayoutPoolLogsDTO): Promise<PayoutPoolLogsResponse> =>
    requests.get(`projects/${projectId}/payout-pools`, { params: dto }),
  getPayoutPoolPayouts: async (projectId: string, payoutPoolId: string): Promise<PayoutPoolPayoutsResponse> =>
    requests.get(`projects/${projectId}/payout-pools/${payoutPoolId}/payouts`),
  getPayoutPoolDetails: async (projectId: string, payoutPoolId: string): Promise<PayoutPoolDetailsResponse> =>
    requests.get(`projects/${projectId}/payout-pools/${payoutPoolId}`),
  getPayoutPoolAttributions: async (
    projectId: string,
    payoutPoolId: string,
    userIdentifier?: string,
  ): Promise<PayoutPoolItemsResponse> =>
    requests.get(`projects/${projectId}/payout-pools/${payoutPoolId}/items`, {
      params: { user_identifier: userIdentifier },
    }),
};
